import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useApiClient } from '@/shared/api'
import type { ListMetrics } from '../ui/CampaignContactLists'
import type { CallDistribution } from '../ui/CallDurations'

interface DialerConfig {
  maxActiveConferences: number
  maxSimultaneousCalls: number
  ratio: number
  maxDialPerSecond: number
}

interface DialerMetrics {
  ringingCalls: number
  connectedCalls: number
  connectedTransfers: number
  inProgressQualificationCallsOver30: number
  dialedToday: number
  connectedToday: number
  transferredToday: number
  completedQualificationCallsOver30: number
  leadsInQueue: number
  connectionRate: number
  dropRate: number
}

export interface DialerCampaign {
  id: number
  name: string
  telephony: string
  isStarted: boolean
  isSyncInProgress: boolean
  hasCallerIds: boolean
  hasActiveListsWithLeads: boolean
  instancesOnline?: boolean
  config: DialerConfig
  metrics: DialerMetrics
  callDistribution?: CallDistribution
  contactLists: ListMetrics[]
}

interface UseDialerCampaignResult {
  campaign: DialerCampaign | null
  initialLoading: boolean
  isOperating: boolean
  toggleCampaignStatus: () => Promise<void>
  toggleListStatus: (listId: number, newStatus: boolean) => Promise<void>
  refreshCampaign: () => void
}

export function useDialerCampaign(campaignId: number): UseDialerCampaignResult {
  const apiClient = useApiClient()
  const [initialLoading, setInitialLoading] = useState(true)
  const [campaign, setCampaign] = useState<DialerCampaign | null>(null)
  const [isOperating, setIsOperating] = useState(false)

  const abortControllerRef = useRef<AbortController | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Single fetch function - the only place that fetches campaign data
  const fetchCampaign = useCallback(
    async (force = false) => {
      if (isOperating && !force) {
        return
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      abortControllerRef.current = new AbortController()

      try {
        const data = await apiClient<DialerCampaign>(
          `/dialer/campaign?campaign_id=${campaignId}`,
          {
            signal: abortControllerRef.current.signal,
          }
        )

        setCampaign((prevCampaign) => {
          // If we're in the middle of an operation, preserve the optimistic state
          if (isOperating && prevCampaign) {
            return {
              ...data,
              isStarted: prevCampaign.isStarted,
              contactLists: prevCampaign.contactLists,
            }
          }
          return data
        })

        setInitialLoading(false)
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.error('Error fetching campaign:', error)
        }
      }
    },
    [campaignId, apiClient, isOperating]
  )

  // Start/stop polling
  const startPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    if (!isOperating) {
      intervalRef.current = setInterval(() => {
        fetchCampaign()
      }, 5000)
    }
  }, [fetchCampaign, isOperating])

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Campaign start/stop with optimistic updates
  const toggleCampaignStatus = useCallback(async () => {
    if (!campaign || isOperating) return

    const newIsStarted = !campaign.isStarted

    // Optimistic update
    setCampaign((prev) => (prev ? { ...prev, isStarted: newIsStarted } : null))
    setIsOperating(true)
    stopPolling()

    try {
      await apiClient('/dialer/campaign/status', {
        method: 'PUT',
        body: JSON.stringify({
          campaignId: campaign.id,
          isStarted: newIsStarted,
        }),
      })

      // Update with confirmed state
      setCampaign((prev) => (prev ? { ...prev, isStarted: newIsStarted } : null))
    } catch (error) {
      // Revert optimistic update on error
      setCampaign((prev) => (prev ? { ...prev, isStarted: !newIsStarted } : null))
      console.error('Failed to update campaign status:', error)
    } finally {
      // Add a small delay before resuming operations to avoid conflicts
      setTimeout(() => {
        setIsOperating(false)
      }, 200)
    }
  }, [campaign, apiClient, isOperating, stopPolling])

  // List toggle with optimistic updates
  const toggleListStatus = useCallback(
    async (listId: number, newStatus: boolean) => {
      if (!campaign || isOperating) return

      // Optimistic update
      setCampaign((prev) => {
        if (!prev) return null
        return {
          ...prev,
          contactLists: prev.contactLists.map((list) =>
            list.id === listId ? { ...list, isActive: newStatus } : list
          ),
        }
      })

      setIsOperating(true)
      stopPolling()

      try {
        await apiClient('/dialer/campaign/lists', {
          method: 'PUT',
          body: JSON.stringify({
            listId,
            isActive: newStatus,
          }),
        })

        // Refresh campaign data to get updated metrics
        await fetchCampaign(true)
      } catch (error) {
        // Revert optimistic update on error
        setCampaign((prev) => {
          if (!prev) return null
          return {
            ...prev,
            contactLists: prev.contactLists.map((list) =>
              list.id === listId ? { ...list, isActive: !newStatus } : list
            ),
          }
        })
        console.error('Failed to update list status:', error)
      } finally {
        setIsOperating(false)
      }
    },
    [campaign, fetchCampaign, apiClient, isOperating, stopPolling]
  )

  // Initial fetch and polling setup
  useEffect(() => {
    fetchCampaign()
    startPolling()

    return () => {
      stopPolling()
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchCampaign, startPolling, stopPolling])

  // Restart polling when operations finish
  useEffect(() => {
    if (!isOperating) {
      startPolling()
    } else {
      stopPolling()
    }
  }, [isOperating, startPolling, stopPolling])

  return useMemo(
    () => ({
      campaign,
      initialLoading,
      isOperating,
      toggleCampaignStatus,
      toggleListStatus,
      refreshCampaign: () => fetchCampaign(true),
    }),
    [campaign, initialLoading, isOperating, toggleCampaignStatus, toggleListStatus, fetchCampaign]
  )
}